import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CircleNotch, Lightbulb, LightbulbFilament } from "@phosphor-icons/react";

function formatTime(iso, tz = "Europe/Madrid") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

function formatDateFull(iso, tz = "Europe/Madrid") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [j, d] = await Promise.all([api.scheduledJobs(), api.dashboard()]);
      setJobs(j);
      setBookings(d.bookings || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <CircleNotch size={32} className="animate-spin text-zinc-600" />
      </div>
    );
  }

  // Group bookings by date
  const byDate = {};
  bookings.forEach((b) => {
    if (!b.start) return;
    const d = b.start.slice(0, 10);
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(b);
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] text-zinc-500 tracking-[0.3em] uppercase font-mono mb-1">
          Programación
        </div>
        <h1 className="font-display font-black text-3xl tracking-tight">Horarios de luces</h1>
        <p className="text-sm text-zinc-400 mt-2">
          Eventos de encendido y apagado programados según las reservas de Playtomic.
        </p>
      </div>

      {/* Scheduled jobs in queue */}
      <div>
        <h2 className="font-display font-bold text-xl mb-3">Cola de eventos ({jobs.length})</h2>
        {jobs.length === 0 ? (
          <div className="bg-[#121212] border border-zinc-800 rounded-md p-6 text-center text-zinc-500 text-sm">
            No hay eventos programados. Pulsa "Sincronizar" en el panel para forzar una sincronización.
          </div>
        ) : (
          <div className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-black/40 border-b border-zinc-800">
                <tr>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Próxima ejecución</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Acción</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-mono">Pista</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const isOn = j.id.includes("light-on-");
                  const courtId = j.args[0];
                  return (
                    <tr key={j.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                      <td className="px-4 py-3 text-sm font-mono text-zinc-300">
                        {formatDateFull(j.next_run)} · {formatTime(j.next_run)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono font-bold ${
                            isOn ? "bg-[#CCFF00]/20 text-[#CCFF00]" : "bg-zinc-900 text-zinc-400"
                          }`}
                        >
                          {isOn ? <LightbulbFilament size={12} weight="fill" /> : <Lightbulb size={12} />}
                          {isOn ? "ENCENDER" : "APAGAR"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-bold">{courtId}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bookings grouped by day */}
      <div>
        <h2 className="font-display font-bold text-xl mb-3">Reservas por día</h2>
        {Object.keys(byDate).length === 0 ? (
          <div className="bg-[#121212] border border-zinc-800 rounded-md p-6 text-center text-zinc-500 text-sm">
            No hay reservas sincronizadas.
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(byDate).map(([d, items]) => (
              <div key={d} className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden">
                <div className="bg-black/40 border-b border-zinc-800 px-4 py-2 flex items-center justify-between">
                  <div className="font-display font-bold capitalize">{formatDateFull(d + "T12:00:00")}</div>
                  <div className="text-xs text-zinc-500 font-mono">{items.length} reservas</div>
                </div>
                <div className="divide-y divide-zinc-900">
                  {items.map((b, idx) => (
                    <div key={idx} className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-bold text-sm">{b.resource_name}</div>
                        <div className="text-xs text-zinc-500 font-mono">
                          {formatTime(b.start)} – {formatTime(b.end)}
                        </div>
                      </div>
                      <div className="text-xs font-mono">
                        {b.skipped_reason ? (
                          <span className="text-zinc-500">{b.skipped_reason}</span>
                        ) : (
                          <span>
                            <span className="text-[#CCFF00]">ON {formatTime(b.light_on_at)}</span>
                            <span className="text-zinc-600 mx-2">→</span>
                            <span className="text-zinc-400">OFF {formatTime(b.light_off_at)}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
